import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, getAuthToken } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CheckCircle2, XCircle, Eye, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/utils";

const authHeaders = () => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } });

export default function DiscountApprovals() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectRemarks, setRejectRemarks] = useState({});

  const fetchPending = async () => {
    try {
      const res = await axios.get(`${API}/quotations/pending-approvals`, authHeaders());
      setRows(res.data || []);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error loading pending approvals"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPending(); }, []);

  const handleApprove = async (id) => {
    try {
      await axios.post(`${API}/quotations/${id}/approve`, {}, authHeaders());
      toast.success("Quotation approved. The creator can now finalize it.");
      fetchPending();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error approving quotation"));
    }
  };

  const handleReject = async (id) => {
    try {
      await axios.post(`${API}/quotations/${id}/reject`, { remarks: rejectRemarks[id] || "" }, authHeaders());
      toast.success("Quotation rejected. It stays as a Draft for the creator to revise.");
      fetchPending();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error rejecting quotation"));
    }
  };

  const fmtMoney = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="discount-approvals-page">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-amber-100">
          <ShieldAlert className="h-5 w-5 text-amber-700" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-[#0F0F0F]" data-testid="discount-approvals-title">Discount Approvals</h1>
          <p className="text-muted-foreground text-sm">Quotations exceeding the permitted discount limit awaiting your approval</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pending Approvals ({rows.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Draft ID</TableHead>
                  <TableHead>Seller</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead>Requested On</TableHead>
                  <TableHead>Invoice Value</TableHead>
                  <TableHead>Overall %</TableHead>
                  <TableHead>Brand-wise %</TableHead>
                  <TableHead>Exceeding</TableHead>
                  <TableHead>Approval Reason</TableHead>
                  <TableHead>Remarks</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-10 text-muted-foreground" data-testid="no-pending-approvals">
                      No quotations are pending approval right now.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((q) => {
                    const info = q.approval_info || {};
                    const brandDisc = info.requested_brand_discounts || {};
                    const exceeding = info.brands_exceeding || [];
                    return (
                      <TableRow key={q.id} data-testid={`approval-row-${q.id}`}>
                        <TableCell className="font-mono">
                          <Badge variant="secondary" className="bg-amber-100 text-amber-800">{q.quotation_id}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">{q.seller_name}</TableCell>
                        <TableCell className="text-sm">{info.requested_by_name || q.created_by_name || "-"}</TableCell>
                        <TableCell className="text-sm">{fmtDate(info.requested_at)}</TableCell>
                        <TableCell className="font-mono">{fmtMoney(q.total_invoice_value)}</TableCell>
                        <TableCell className="font-mono font-semibold text-red-600" data-testid={`approval-overall-${q.id}`}>
                          {Number(info.requested_overall_discount ?? q.effective_discount ?? 0).toFixed(2)}%
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-[220px]">
                            {Object.entries(brandDisc).map(([b, pct]) => (
                              <span
                                key={b}
                                className={`text-[11px] px-1.5 py-0.5 rounded border ${exceeding.includes(b) ? "bg-red-50 border-red-200 text-red-700 font-semibold" : "bg-slate-50 border-slate-200 text-slate-600"}`}
                              >
                                {b}: {Number(pct).toFixed(1)}%
                              </span>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          {exceeding.length > 0
                            ? <span className="text-xs text-red-600 font-medium">{exceeding.join(", ")}</span>
                            : <span className="text-xs text-muted-foreground">Overall</span>}
                        </TableCell>
                        <TableCell className="max-w-[210px]" data-testid={`approval-reasons-${q.id}`}>
                          <div className="flex flex-col gap-1">
                            {(info.approval_reasons && info.approval_reasons.length > 0
                              ? info.approval_reasons
                              : ["Discount % Limit Exceeded"]).map((r) => (
                              <span
                                key={r}
                                className={`text-[11px] px-1.5 py-0.5 rounded border w-fit ${r.includes("Contribution") ? "bg-purple-50 border-purple-200 text-purple-700" : "bg-red-50 border-red-200 text-red-700"}`}
                              >
                                {r}
                              </span>
                            ))}
                            {info.contribution && info.contribution.projected != null && info.contribution.exceeds && (
                              <span className="text-[11px] text-purple-700">
                                Contribution {Number(info.contribution.projected).toFixed(2)}% (limit {Number(info.contribution.limit ?? 25).toFixed(0)}%)
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm max-w-[200px]">
                          <span className="line-clamp-2" title={info.reason || ""}>{info.reason || "-"}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/quotations/${q.id}`)} title="View" data-testid={`approval-view-${q.id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="ghost" className="text-green-600 hover:text-green-700" title="Approve" data-testid={`approval-approve-${q.id}`}>
                                  <CheckCircle2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Approve this discount?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Approving {q.quotation_id} for <b>{q.seller_name}</b> at an overall discount of{" "}
                                    <b>{Number(info.requested_overall_discount ?? 0).toFixed(2)}%</b> will allow the creator to finalize it. No values will be recalculated.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleApprove(q.id)} className="bg-green-600 hover:bg-green-700" data-testid={`approval-approve-confirm-${q.id}`}>
                                    Approve
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" title="Reject" data-testid={`approval-reject-${q.id}`}>
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Reject this quotation?</AlertDialogTitle>
                                  <AlertDialogDescription asChild>
                                    <div className="space-y-2">
                                      <p>{q.quotation_id} will be rejected and stay as a Draft. The creator can edit and resubmit it.</p>
                                      <Textarea
                                        placeholder="Rejection remarks (optional)…"
                                        value={rejectRemarks[q.id] || ""}
                                        onChange={(e) => setRejectRemarks((prev) => ({ ...prev, [q.id]: e.target.value }))}
                                        data-testid={`approval-reject-remarks-${q.id}`}
                                      />
                                    </div>
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleReject(q.id)} className="bg-destructive hover:bg-destructive/90" data-testid={`approval-reject-confirm-${q.id}`}>
                                    Reject
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
